import { FatalAttraction as FA } from '@vpr/tracking';

import {
  AlbumLayouts,
  Clip,
  ClipDataObject,
  Pictures,
  Schedule,
  ScheduleFrequencyType,
} from '../types';
import {
  ALBUM_KEY,
  LAYOUT_GRID,
  LAYOUT_LIVE,
  LAYOUT_PLAYER,
  USER_KEY,
  VIDEO_KEY,
  VIMEO_BLUE,
} from './constants';
import {
  DAY_TO_NUMBER_MAPPING,
  DEFAULT_CUSTOMIZATIONS,
  LIVE_EVENTS_KEY,
  VIMEO_DEV_DOMAIN,
  VIMEO_PROD_DOMAIN,
} from '../utils/constants';

export function getUserId(userUri?: string): string | null {
  if (!userUri) {
    return null;
  }

  const userIdMatch = userUri.match(/^([0-9]+)$/);
  if (userIdMatch) {
    return userIdMatch[1];
  }

  const userUriMatch = userUri.match(/users\/([0-9]+)/);
  if (userUriMatch) {
    return userUriMatch[1];
  }

  return null;
}

/**
 * Prepare videos array to be displayed in masonry style grid.
 * Divide into odd and even indices and combine.
 *
 * @param {Array} array - The list of videos to reorder
 * @return {Object} object with sets of arrays for columns
 */
export const prepareArrayForColumns = (array: Clip[]) => {
  const initialValue = { evenIndexArray: [], oddIndexArray: [] };
  if (!array) {
    return initialValue;
  }

  return array.reduce((previousValue, currentValue, index) => {
    const key = index % 2 === 0 ? 'evenIndexArray' : 'oddIndexArray';
    return {
      ...previousValue,
      [key]: [...previousValue[key], currentValue],
    };
  }, initialValue);
};

/**
 * Get resource id from single level resource uri (i.e - /videos/ or /users/)
 * @param {String} uri - uri of the resource
 * @param {String} resourceType - resource whose id is being parsed
 */
export const idFromUri = (uri: string, resourceType: string) => {
  if (!uri) {
    return 0;
  }

  const resourcePath = {
    [ALBUM_KEY]: '/albums/',
    [USER_KEY]: '/users/',
    [VIDEO_KEY]: '/videos/',
    [LIVE_EVENTS_KEY]: '/live_events/',
  };

  try {
    const path = (resourcePath as any)[resourceType];
    if (!path || !uri.includes(path)) {
      return 0;
    }

    return parseInt(uri.split(path)[1], 10);
  } catch (error) {
    return 0;
  }
};

export const getVideoId = (video: ClipDataObject) => {
  const PRIVACY_UNLISTED = 'unlisted';
  const id = video.uri.split('videos/')[1];
  // Unlisted videos retain hash in the uri, it has to be removed to obtain the true id.
  if (video.privacy.view === PRIVACY_UNLISTED) {
    return Number(id.split(':')[0]);
  }
  return Number(id);
};

// DB value should not include #
export const convertColorForDb = (hexColor: string) => hexColor.replace('#', '');

export const convertColorForDisplay = (hexNumber: string | null = '00adef') => {
  return hexNumber !== null ? `#${hexNumber}` : VIMEO_BLUE;
};

/**
 * Get raw image path from uploaded asset url - TEMPORARY NEED
 * @param {Object} upload - Image url
 */
export const getRawUploadedImageUrl = (upload: Pictures) => {
  const baseLink = upload.baseLink;
  if (baseLink) {
    return baseLink;
  }

  if (!upload.sizes) {
    return '';
  }

  const image = upload.sizes[0];

  const { height, width } = image;

  return image.link.split(`_${width}x${height}`)[0];
};

export const isSmartPhoneOrTablet = () => {
  return /iphone|ipod|ipad|android|webos|blackberry|windows phone|iemobile|opera mini/i.test(
    navigator.userAgent.toLowerCase(),
  );
};

/**
 * Has the user toggled any of the customizations?
 */
export const isAlbumCustomized = (fields: {}) => {
  for (const val in DEFAULT_CUSTOMIZATIONS) {
    if ((fields as any)[val] && (fields as any)[val] !== (DEFAULT_CUSTOMIZATIONS as any)[val]) {
      return true;
    }
  }
  return false;
};

/**
 * Is the album owner previewing customization?
 */
export const hasPreviewParam = () => {
  const { preview } = parseQuery(window.location.search);
  return preview === 'true';
};

export const getTimecodeFromSeconds = (num: number, format: string) => {
  let _format = format;

  if (num === 0) {
    return '00:00';
  }

  if (!_format) {
    _format = num >= 60 * 60 * 60 ? 'hh:mm:ss' : 'mm:ss';
  }

  const formattedTime: (number | string)[] = [];
  const parts = _format ? _format.split(':') : [];
  let hours;
  let minutes;
  let seconds;

  let i = 0;
  while (i < parts.length) {
    switch (parts[i]) {
      case 'h':
        hours = Math.floor(num / 60 / 60);
        formattedTime.push(hours);
        break;

      case 'hh':
        hours = Math.floor(num / 60 / 60);
        if (hours < 10) {
          hours = '0' + hours;
        }
        formattedTime.push(hours);
        break;

      case 'm':
        minutes = hours ? Math.floor((num / 60) % 60) : Math.floor(num / 60);
        formattedTime.push(minutes);
        break;

      case 'mm':
        minutes = hours ? Math.floor((num / 60) % 60) : Math.floor(num / 60);
        if (minutes < 10) {
          minutes = '0' + minutes;
        }
        formattedTime.push(minutes);
        break;

      case 'ss':
        seconds = Math.floor(num % 60);
        if (seconds < 10) {
          seconds = '0' + seconds;
        }
        formattedTime.push(seconds);
        break;

      default:
        formattedTime.push('');
        break;
    }
    i = i + 1;
  }

  return formattedTime.join(':');
};

/**
 * Track any potential API errors
 * @param res - API Response
 * @param methodName - Function where request was made
 */
export const trackPotentialError = (res?: any, methodName: string = 'unknown_method') => {
  if (res && res.errorCode) {
    FA.trackEvent({
      container: 'showcase_api_error',
      component: methodName,
      keyword: res.errorCode,
    });
  }
};

/**
 * Parse query params
 * @param search - Query string
 */
export const parseQuery = (search: string): { [x: string]: string } => {
  return search
    .substr(1)
    .split('&')
    .map((q) => q.split('='))
    .reduce((memo, [key, value]) => {
      return {
        ...memo,
        [key]: value,
      };
    }, {});
};

export type AlbumQuery = {
  page?: string;
  video?: string;
};

/**
 * Convert object of params to encoded URI string
 * @param object - Object of query
 */
export const stringifyQuery = (object: AlbumQuery) => {
  const keys = Object.keys(object);

  return keys
    .map((key) => {
      const value = (object as any)[key];
      if (!value) {
        return '';
      }
      return `${key}=${value}`;
    })
    .filter((item) => item.length > 0)
    .join('&');
};

/**
 * Get page size based on layout
 * @param {AlbumLayouts} layout - Album layout
 * @return {number} - Page size
 */
export const getLayoutPageSize = (layout: AlbumLayouts) => {
  switch (layout) {
    case LAYOUT_PLAYER:
      return 24;
    case LAYOUT_GRID:
    case LAYOUT_LIVE:
    default:
      return 12;
  }
};

/**
 * Truncates a passed-in string (if longer than length)
 * to either the length or the last empty space character, followed by ellipsis
 */
export const truncate = (original: string, length: number) => {
  if (original.length <= length) {
    return original;
  }

  const shortened = original.substring(0, length);

  const len =
    shortened.lastIndexOf(' ') > -1 ? Math.min(length, shortened.lastIndexOf(' ')) : length;

  return `${shortened.substr(0, len)}...`;
};

/**
 * Get default thumbnail URI for a showcase
 * @param {Pictures | Pictures[]} pictures - Pictures or array of Pictures related to the album
 * @return {string | undefined} - URI of the default thumbnail.
 */
export const getDefaultThumbnailUri = (pictures: Pictures | Pictures[]): string | undefined => {
  const targetPictures = Array.isArray(pictures) ? pictures.filter((pic) => !!pic.uri) : [pictures];

  // If there is more than one picture, return undefined, as no image set for the Showcase.
  if (targetPictures.length > 1) {
    return;
  }
  const pictureThumbnails = targetPictures.flatMap((pic) => pic.sizes);
  return pictureThumbnails[pictureThumbnails.length - 1]?.link;
};

/**
 * Get schedule frequency of an Associated live event within a showcase
 * @param {Schedule} schedule - Schedule object of the associated live event
 * @return {string} - schedule frequency of the event(like weekly, monthly, yearly).
 */
export const getEventScheduleFrequency = (schedule: Schedule): string => {
  const { rrule } = schedule;
  if (!rrule) {
    return '';
  }
  // rrule: "RRULE:FREQ=WEEKLY;BYDAY=MO,SU,TU,WE,TH,FR,SA"
  // rrule will mandatorily contain the frequency of the schedule like weekly, monthly and yearly
  const frequencyString = rrule.split('FREQ=')[1].split(';')[0];
  return frequencyString.toLowerCase();
};

/**
 * Get schedule weekdays of an Associated live event within a showcase
 * @param {Schedule} schedule - Schedule object of the associated live event
 * @return {number[]} - schedule weekdays of the event.
 */
export const getEventScheduleWeekDays = (schedule: Schedule) => {
  // rrule might not contain BYDAY related information for weekly schedule if no day has been selected by the user. Hence, we need to check for this condition
  if (
    getEventScheduleFrequency(schedule) !== ScheduleFrequencyType.WEEKLY ||
    !schedule.rrule.includes('BYDAY=')
  ) {
    return [];
  }
  const weekdays = schedule.rrule
    .split('BYDAY=')[1]
    .split(',') as (keyof typeof DAY_TO_NUMBER_MAPPING)[];
  return weekdays.map((day) => DAY_TO_NUMBER_MAPPING[day]);
};

/**
 * Get schedule month of an Associated live event within a showcase
 * @param {Schedule} schedule - Schedule object of the associated live event
 * @return {number} - schedule month of the event.
 */
export const getEventScheduleDayOfMonth = (schedule: Schedule) => {
  const dayOfMonth = schedule.rrule.split('BYMONTHDAY=')[1];
  return Number(dayOfMonth);
};

/**
 * Get domain name
 * @param {boolean} isOffsite - boolean that denotes if showcase is viewed offsite
 * @return {string} - domain name
 */
export const getDomain = (isOffsite?: boolean) => {
  if (isOffsite) {
    return process.env.NODE_ENV === 'development' ? VIMEO_DEV_DOMAIN : VIMEO_PROD_DOMAIN;
  }
  return '';
};
